import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { IDateInterval } from "@components/inputs/date/utils";
import { withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { editableAmountLinkFormatter } from "@components/smart/selectionTableView/SelectionTableView.utils";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import BindingContext from "@odata/BindingContext";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import { EntitySetName, EntityTypeName, VatStatementDocumentEntity } from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode } from "@odata/GeneratedEnums";
import { getDocumentStatusColumnDefinition } from "@pages/documents/DocumentCommonDefs";
import {
    getDocumentStatusEnumsTranslationNamespaces,
    getDocumentStatusFilterDefinition,
    getUsedStatuses
} from "@pages/documents/DocumentDef";
import {
    getCancelReapplyFilters,
    ICancelReapplyTypeArgs,
    TCancelOrReapply
} from "@pages/electronicSubmission/VatSubmission.utils";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { DocumentStatusLocalPath } from "@pages/reports/CommonDefs";
import i18next from "i18next";
import React from "react";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { Sort, TextAlign } from "../../enums";
import { ColoredText } from "../../global.style";
import { ITableStorageDefaultCustomData } from "../../model/TableStorage";
import { formatCurrency } from "../../types/Currency";
import { getUtcDayjs } from "../../types/Date";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";

export const AmountCancelReapplyPath = BindingContext.localContext("AmountCancelReapply");

export const cancelReapplyPairDocumentAmountFormatter: TFormatterFn = (value, args) => {
    const passArgs = {
        ...args,
        item: {
            ...args.item,
            // editableAmountLinkFormatter uses only transaction amount, but we need to work with Amount fields in VAT,
            // so we fake args here
            // todo: refactor editableAmountLinkFormatter to be more generic
            TransactionCurrencyCode: args.item.CurrencyCode,
            TransactionCurrency: {
                Code: args.item.CurrencyCode
            }
        }
    };
    return editableAmountLinkFormatter(passArgs);
};

export interface PairCancelReapplyTableCustomData extends ITableStorageDefaultCustomData, ICancelReapplyTypeArgs {}

export function getDefinitionsFactory(type: TCancelOrReapply, isProcessed: boolean, period: IDateInterval): IGetDefinition {

    const getDefinitions = (context: IAppContext): IDefinition => {
        const filters = getCancelReapplyFilters(type, isProcessed, period);

        const isCancelledType = type === "Cancelled";
        const isUnprocessedCancelled = isCancelledType && !isProcessed;

        const filterBarDef: IFilterGroupDef[] = [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            isValueHelp: true,
            defaultFilters: [
                VatStatementDocumentEntity.NumberOurs,
                VatStatementDocumentEntity.NumberTheirs,
                DocumentStatusLocalPath,
                VatStatementDocumentEntity.BusinessPartner,
                VatStatementDocumentEntity.DateDue,
                VatStatementDocumentEntity.AmountVat,
                VatStatementDocumentEntity.Amount,
            ],
            filterDefinition: {
                [VatStatementDocumentEntity.NumberOurs]: {},
                [VatStatementDocumentEntity.NumberTheirs]: {},
                ...getDocumentStatusFilterDefinition(getUsedStatuses(context)),
                ...withDisplayName("BusinessPartner"),
                [VatStatementDocumentEntity.DateDue]: {},
                [VatStatementDocumentEntity.AmountVat]: {},
                [VatStatementDocumentEntity.Amount]: {}
            }
        }];

        if (!isUnprocessedCancelled) {
            filterBarDef[0].filterDefinition[VatStatementDocumentEntity.VatCancellationAmount] = {};
            filterBarDef[0].defaultFilters.push(VatStatementDocumentEntity.VatCancellationAmount);
        }

        const table: ISplitPageTableDef = {
            filterBarDef,
            id: `${EntityTypeName.VatStatementDocument}Table`,
            preventStoreVariant: true,
            massEditableDef: {},
            initialSortBy: [
                { id: VatStatementDocumentEntity.NumberOurs, sort: Sort.Desc }
            ],
            filter: filters?.length ? filters.join(" and ") : null,
            additionalProperties: [
                { id: VatStatementDocumentEntity.Currency },
                { id: VatStatementDocumentEntity.DocumentTypeCode },
                // we calculate ratio from transaction amounts because AmountDue is only in transaction currency
                { id: VatStatementDocumentEntity.TransactionAmount },
                { id: VatStatementDocumentEntity.TransactionAmountDue },
            ],
            columns: [
                VatStatementDocumentEntity.NumberOurs,
                VatStatementDocumentEntity.NumberTheirs,
                DocumentStatusLocalPath,
                VatStatementDocumentEntity.BusinessPartner,
                VatStatementDocumentEntity.DateDue,
                VatStatementDocumentEntity.AmountVat,
                isUnprocessedCancelled ? AmountCancelReapplyPath : VatStatementDocumentEntity.VatCancellationAmount,
                VatStatementDocumentEntity.Amount,
            ],
            columnDefinition: {
                [VatStatementDocumentEntity.NumberOurs]: {
                    formatter: (_, { item, context }) =>
                            getTableIntentLink(item.NumberOurs, {
                                route: `${getRouteByDocumentType(item.DocumentTypeCode as DocumentTypeCode)}/${item.Id}`,
                                context
                            })
                },
                [VatStatementDocumentEntity.NumberTheirs]: {
                    label: i18next.t("ElectronicSubmission:CancelReapplyDialog.NumberTheirs"),
                },
                ...getDocumentStatusColumnDefinition(),
                ...withDisplayName(VatStatementDocumentEntity.BusinessPartner),
                [VatStatementDocumentEntity.DateDue]: {
                    label: i18next.t("ElectronicSubmission:CancelReapplyDialog.Overdue"),
                    formatter: (_, { item }) => {
                        const dueDays = getUtcDayjs().diff(getUtcDayjs(item.DateDue), "days") ?? 0;
                        const dueText = i18next.t("Common:General.XDays", { count: dueDays });
                        return {
                            value: (
                                    <ColoredText color={"C_SEM_text_warning"}>
                                        {dueText}
                                    </ColoredText>
                            ),
                            tooltip: dueText
                        };
                    }
                },
                [VatStatementDocumentEntity.AmountVat]: {
                    label: i18next.t("ElectronicSubmission:CancelReapplyDialog.AmountVat"),
                    formatter: (val, { item }) =>
                            formatCurrency(val, item.CurrencyCode)
                },
                [VatStatementDocumentEntity.Amount]: {
                    label: i18next.t("ElectronicSubmission:CancelReapplyDialog.Amount"),
                    formatter: (val, { item }) =>
                            formatCurrency(val, item.CurrencyCode)
                },
                ...(isUnprocessedCancelled ? {
                    [AmountCancelReapplyPath]: {
                        label: i18next.t("ElectronicSubmission:CancelReapplyDialog.CancellationAmount"),
                        textAlign: TextAlign.Right,
                        width: "140px",
                        fieldSettings: {
                            disableSort: true,
                        },
                        formatter: cancelReapplyPairDocumentAmountFormatter
                    }
                } : {
                    [VatStatementDocumentEntity.VatCancellationAmount]: {
                        label: i18next.t(`ElectronicSubmission:CancelReapplyDialog.${isCancelledType ? "CancellationAmount" : "CancellationAmountReapplied"}`),
                        formatter: (val, { item }) =>
                                formatCurrency(val, item.CurrencyCode)
                    }
                })
            }
        };

        return {
            entitySet: EntitySetName.VatStatementDocuments,
            table
        };
    };

    getDefinitions.translationFiles = [
        "ElectronicSubmission", "InvoicesReceived", "OtherLiabilities",
        "Common", "Components", "NumberRange", "Error", "Enums",
        ...getDocumentStatusEnumsTranslationNamespaces()
    ];

    return getDefinitions;
}
